import React, { useContext } from 'react'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { ActivityIndicator, Image, View } from 'react-native'
import { ImageStyle } from 'react-native-fast-image'
import { BottomSheet } from '@src/components'
import { Text } from '@src/components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import { AppStackParamList } from '@src/navigation/types'
import { NavigatorContext, useGoBack, useSnack } from '@src/utils'
import { Device, ErrorMessages } from '@src/config'
import { topPriorityCoachAssignmentSelector } from '@screens/NutritionistHub/models/nutritionistHub.selectors.ts'
import { nutritionistImage } from '@assets/images'
import { ImageWithPlaceholder } from '@components/Image/FastImageWithPlaceholder.tsx'

const NUTRITIONIST_PHOTO_SIZE = 128
const defaultNutritionistPhotoUri = Device.web
  ? null
  : Image.resolveAssetSource(nutritionistImage.imageSource).uri

export const LateCancelOrReschedulePaidVideoCallModal = () => {
  const styles = useStyleSheet(themedStyles)
  const dispatch = useDispatch()
  const goBack = useGoBack()
  const route = useRoute<RouteProp<AppStackParamList, 'LateCancelOrReschedulePaidVideoCallModal'>>()

  const { videoCallId, useRescheduleDescription } = route.params
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const showSnack = useSnack()
  const coachAssignment = useSelector(topPriorityCoachAssignmentSelector)
  const nutritionistPhotoUri =
    coachAssignment?.coach?.coachProfile?.photo || defaultNutritionistPhotoUri

  const onCancelPress = () => {
    enableLoader()
    dispatch({
      type: 'nutritionistHub/cancelAppointment',
      payload: {
        id: videoCallId,
      },
      success: () => {
        navigation.replace('Drawer', { screen: 'Dashboard', params: { screen: 'NutritionistHub' } })
      },
      failure: (error: any) => {
        const errorMessage = error?.message || ErrorMessages.ServerError
        showSnack(errorMessage, null, 'error')
      },
      complete: () => {
        disableLoader()
      },
    })
  }

  return (
    <BottomSheet
      primaryButton={{ text: 'Cancel and Use Credit', onPress: onCancelPress }}
      secondaryButton={{ text: 'Keep My Call', onPress: goBack }}
      buttonDirection="column-reverse"
    >
      {nutritionistPhotoUri && (
        <View style={styles.nutritionistPhotoContainer}>
          <ImageWithPlaceholder
            style={styles.nutritionistPhoto as ImageStyle}
            source={{ uri: nutritionistPhotoUri }}
            placeholderComponent={<ActivityIndicator animating size="small" {...styles.spinner} />}
          />
        </View>
      )}
      <Text style={styles.title} type="title-2">
        Need to Cancel?
      </Text>
      {useRescheduleDescription ? (
        <Text style={styles.content} type="regular">
          Rescheduling isn’t available within 24 hours of your scheduled call. Cancelling now will
          use one of your coaching credits. If you have another credit available, you can book a new
          call.
        </Text>
      ) : (
        <Text style={styles.content} type="regular">
          Cancelling now will use one of your coaching credits. If you have another credit
          available, you can book a new call.
        </Text>
      )}
    </BottomSheet>
  )
}

const themedStyles = StyleService.create({
  title: {
    textAlign: 'center',
    paddingTop: 16,
    paddingBottom: 16,
  },
  content: {
    textAlign: 'center',
  },
  nutritionistPhotoContainer: {
    marginVertical: 16,
    alignSelf: 'center',
    zIndex: 99,
  },
  nutritionistPhoto: {
    width: NUTRITIONIST_PHOTO_SIZE,
    height: NUTRITIONIST_PHOTO_SIZE,
    borderRadius: NUTRITIONIST_PHOTO_SIZE / 2,
  },
  spinner: {
    color: 'theme.surface.base',
  },
})
